import {Injectable} from '@angular/core';
import {MenuConfiguration, UserDto } from '../models/User';
import {JwtHelperService} from '@auth0/angular-jwt';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../environments/environment';
import * as Constants from '../models/Constants';
import {ErisLoginService} from './sso/eris-login.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserOrganization } from '../models/ApplicationModel';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  baseUrl = environment.baseUrl;

  sendResetPasswordLinkURL = '/Account/SendResetPasswordLink/';
  validateResetTokenURL = '/Account/ValidateResetToken';
  resetPasswordURL = '/Account/ResetPassword';

  public abroadCgmpApplicantTypes = [Constants.APPLICANT_TYPE_MANUFACTURER, Constants.LOCAL_AGENT, Constants.LICENSE_HOLDER,
    Constants.REPRESENTATIVE];

  constructor(private http: HttpClient, private erisLoginService: ErisLoginService) {
  }

  storeUserData(data) {
    localStorage.setItem('user_data', JSON.stringify(data));
  }

  storeMenuConfig(data) {
    localStorage.setItem('menu', JSON.stringify(data));
  }

  getUserData(): UserDto {
    return JSON.parse(localStorage.getItem('user_data'));
  }

  getMenu(): MenuConfiguration[] {
    const raw = localStorage.getItem('menu');
    if (!raw) {
      return null;
    }
    const menu = JSON.parse(raw);
    // Treat empty array as missing so a failed menus call does not stick forever.
    if (!menu || (Array.isArray(menu) && menu.length === 0)) {
      localStorage.removeItem('menu');
      return null;
    }
    return menu;
  }

  getUserTinData(): UserOrganization[] {
    return JSON.parse(localStorage.getItem('tin_data'));
  }

  storeUserTinData(data) {
    localStorage.setItem('tin_data', JSON.stringify(data));
  }

  getUserRoleCodes(): String[] {
    const currentUser : UserDto = this.getUserData();
    return currentUser ? currentUser.roles.map(r => r.code) : [];
  }

  getTokenData(): string {
    return this.erisLoginService.getToken();
  }

  removeData() {
    localStorage.removeItem('user_data');
    localStorage.removeItem('menu');
    localStorage.removeItem('tin_data');
  }

  sendResetPasswordLink(username) {
    return this.http.get<any>(this.baseUrl + this.sendResetPasswordLinkURL + username);
  }

  validateResetToken(id, token) {
    const resetTokenData = {'id': id, 'token': token};
    return this.http.post<any>(this.baseUrl + this.validateResetTokenURL, resetTokenData, {});
  }

  resetPassword(passwordResetData) {
    return this.http.post<any>(this.baseUrl + this.resetPasswordURL, passwordResetData, {});
  }

  isUserTeamLeader(): boolean {
    return (this.getUserRoleCodes().indexOf(Constants.CSO_TEAM_LEADER) !== -1 ||
      this.getUserRoleCodes().indexOf(Constants.INSPECTOR_TEAM_LEADER) !== -1);
  }

  isUserInspectionTeamLeader(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.INSPECTOR_TEAM_LEADER) !== -1;
  }

  isUserCSOTeamLeader(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.CSO_TEAM_LEADER) !== -1;
  }

  isUserDirector(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.DIRECTOR) !== -1;
  }

  isUserCSO(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.CSO) !== -1;
  }

  isUserInspector(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.INSPECTOR) !== -1;
  }

  isUserApplicant(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.APPLICANT) !== -1;
  }

  isUserRegistrant(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.REGISTRANT) !== -1;
  }

  isUserGMPCoordinator(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.GMP_COORDINATOR) !== -1;
  }

  isUserGMPOfficer(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.GMP_OFFICER) !== -1;
  }

  isUserGMPInspector(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.CGMP_INSPECTOR) !== -1;
  }
  
  isUserSuperAdmin(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.SUPER_ADMIN) !== -1;
  }

  isUserTaskForce(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.GMP_TASK_FORCE) !== -1;
  }

  isUserFinancialOfficer(): boolean {
    return this.getUserRoleCodes().indexOf(Constants.FINANCIAL_OFFICER) !== -1;
  }

  isNotApplicant(): boolean {
    return !(this.getUserRoleCodes().length === 1 && this.getUserRoleCodes().indexOf(Constants.APPLICANT) !== -1);
  }

  getUserDepartmentCodes(): String[] {
    const currentUser : UserDto = this.getUserData();
    return currentUser ? currentUser.departments.map(u => u.code) : [];
  }

  isApplicantFocalPerson() : boolean {
    const currentUser : UserDto = this.getUserData();
    return currentUser ? (currentUser.applicantType?.code === Constants.FOCAL_PERSON) : false;
  }

  getUserApplicantType() : string {
    const currentUser = this.getUserData();
    return currentUser ? currentUser.applicantType.name : null;
  }

  getUserBranchOffice() {
    const currentUser = this.getUserData();
    return currentUser.branchOffice;
  }

  isUserAbroadCGMPApplicant(user: UserDto){
    if(user.applicantType && this.abroadCgmpApplicantTypes.indexOf(user.applicantType.code) != -1){
      return true
    }
    return false;
  }

  getVerifaydaToken(endpoint, code) {
    return this.http.post<any>(this.baseUrl + endpoint, code);
  }

getFaydaUserInfo(endpoint: string, accessToken): Observable<string> {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${accessToken}`
  });

  return this.http.post(this.baseUrl + endpoint, accessToken, {
    headers,
    responseType: 'text' as const  // ✅ this makes TypeScript happy
  });
}
}
